import { useEffect, useState } from "react";
import api, { ROLES, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Mail, Eraser, KeyRound, Copy, Loader2, Pencil, UserX, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { Modal, In, Sel } from "./Projects";
import CleanupModal from "@/components/CleanupModal";

const empty = { name: "", email: "", role: "auxiliar", phone: "" };

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [feedback, setFeedback] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [openCleanup, setOpenCleanup] = useState(false);
  const [resetInfo, setResetInfo] = useState(null); // {name, email, reset_link, email_sent}
  const [resetLoading, setResetLoading] = useState(null); // user id
  const [editUser, setEditUser] = useState(null); // {id, name}
  const [editSaving, setEditSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFeedback(null);
    try {
      const { data } = await api.post("/users", form);
      setFeedback({
        ok: true,
        msg: data.email_sent
          ? `Invitación enviada a ${data.email}. El usuario recibirá un correo para activar su cuenta.`
          : `Usuario creado. ⚠ El correo no pudo enviarse. Comparte este link manualmente:\n${data.activation_link || "(no link)"}`,
      });
      setForm(empty);
      load();
    } catch (err) {
      setFeedback({ ok: false, msg: err.response?.data?.detail || "Error al crear usuario" });
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar usuario?")) return;
    await api.delete(`/users/${id}`);
    load();
  };

  const toggleActive = async (u) => {
    const suspend = u.active !== false;
    if (!window.confirm(suspend
      ? `¿Suspender el acceso de ${u.name}? Su cuenta e historial se conservan, pero no podrá iniciar sesión.`
      : `¿Reactivar el acceso de ${u.name}?`)) return;
    try {
      await api.patch(`/users/${u.id}`, { active: !suspend ? true : false });
      load();
    } catch (err) {
      setFeedback({ ok: false, msg: err.response?.data?.detail || "Error al actualizar" });
    }
  };

  const sendReset = async (u) => {
    if (!window.confirm(`¿Enviar enlace de recuperación de contraseña a ${u.name} (${u.email})?`)) return;
    setResetLoading(u.id);
    try {
      const { data } = await api.post(`/users/${u.id}/reset-link`);
      setResetInfo(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al generar el enlace");
    } finally {
      setResetLoading(null);
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editUser.name.trim()) return;
    setEditSaving(true);
    try {
      await api.patch(`/users/${editUser.id}`, { name: editUser.name.trim() });
      toast.success("Nombre actualizado");
      setEditUser(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    } finally {
      setEditSaving(false);
    }
  };

  const roleLabel = (r) => ROLES.find((x) => x.value === r)?.label || r;

  const closeModal = () => { setOpen(false); setFeedback(null); };

  return (
    <div data-testid="users-page">
      <PageHeader
        subtitle="Equipo y permisos"
        title="Personal"
        actions={
          <div className="flex gap-2">
            <button data-testid="open-cleanup-btn" onClick={() => setOpenCleanup(true)}
              className="btn-ghost px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold"
              style={{ borderColor: "#dc2626", color: "#fca5a5" }}>
              <Eraser className="w-4 h-4" /> Limpiar datos
            </button>
            <button data-testid="new-user-btn" onClick={() => { setForm(empty); setFeedback(null); setOpen(true); }} className="btn-primary px-4 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold">
              <Plus className="w-4 h-4" /> Invitar usuario
            </button>
          </div>
        }
      />
      <div className="px-6 lg:px-8 py-6">
        <div className="panel overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="users-table">
            <thead className="panel-header">
              <tr className="text-left">
                <th className="label-mono px-4 py-3">Nombre</th>
                <th className="label-mono px-4 py-3">Email</th>
                <th className="label-mono px-4 py-3">Teléfono</th>
                <th className="label-mono px-4 py-3">Rol</th>
                <th className="label-mono px-4 py-3">Estado</th>
                <th className="label-mono px-4 py-3">Creado</th>
                <th className="label-mono px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 flex items-center justify-center font-bold text-xs rounded-sm font-display"
                        style={{ background: "rgba(34,211,238,0.1)", color: "var(--cyan)", border: "1px solid rgba(34,211,238,0.3)" }}>
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                      <span className="font-semibold text-slate-100">{u.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{u.email}</td>
                  <td className="px-4 py-3 text-slate-300">{u.phone || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-sm uppercase tracking-widest ${u.role === "admin" ? "badge-magenta" : "badge-info"}`}>
                      {roleLabel(u.role)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {u.active === false ? (
                      <span className="px-2 py-0.5 text-[10px] font-semibold rounded-sm uppercase tracking-widest" style={{ color: "#fca5a5", border: "1px solid var(--red)" }} data-testid={`user-suspended-${u.id}`}>Suspendido</span>
                    ) : u.activated === false ? (
                      <span className="px-2 py-0.5 text-[10px] font-semibold rounded-sm uppercase tracking-widest badge-pending">Pendiente</span>
                    ) : (
                      <span className="px-2 py-0.5 text-[10px] font-semibold rounded-sm uppercase tracking-widest badge-success">Activo</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        data-testid={`edit-user-${u.id}`}
                        title="Editar nombre"
                        onClick={() => setEditUser({ id: u.id, name: u.name || "" })}
                        className="p-1 rounded-sm hover:bg-white/5"
                        style={{ color: "var(--cyan)" }}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`reset-password-${u.id}`}
                        title="Enviar enlace para recuperar contraseña"
                        disabled={resetLoading === u.id}
                        onClick={() => sendReset(u)}
                        className="p-1 rounded-sm hover:bg-white/5 disabled:opacity-50"
                        style={{ color: "var(--amber)" }}
                      >
                        {resetLoading === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                      </button>
                      {u.id !== user?.id && (
                        <button
                          data-testid={`toggle-active-${u.id}`}
                          title={u.active === false ? "Reactivar acceso" : "Suspender acceso (sin borrar cuenta ni historial)"}
                          onClick={() => toggleActive(u)}
                          className="p-1 rounded-sm hover:bg-white/5"
                          style={{ color: u.active === false ? "var(--green)" : "#fb923c" }}
                        >
                          {u.active === false ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                        </button>
                      )}
                      {u.id !== user?.id && (
                        <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u.id)} className="p-1 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }}><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {open && (
        <Modal onClose={closeModal} title="Invitar usuario">
          <form onSubmit={save} className="space-y-4">
            <div className="text-xs text-slate-400 px-3 py-2.5 rounded-sm flex gap-2 items-start"
              style={{ background: "var(--bg-2)", border: "1px solid var(--border-2)" }}>
              <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "var(--cyan)" }} />
              <span>Se enviará un correo con un enlace para que el usuario defina su contraseña y active su cuenta.</span>
            </div>
            <In label="Nombre" testId="user-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <In label="Email" testId="user-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <div className="grid grid-cols-2 gap-3">
              <In label="Teléfono" testId="user-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required={false} />
              <Sel label="Rol" testId="user-role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={ROLES} />
            </div>
            {feedback && (
              <div data-testid="user-feedback" className={`text-xs px-3 py-2.5 rounded-sm whitespace-pre-line ${feedback.ok ? "badge-success" : "badge-danger"}`}>
                {feedback.msg}
              </div>
            )}
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={closeModal} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cerrar</button>
              <button type="submit" disabled={submitting} data-testid="save-user-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">
                {submitting ? "Enviando…" : "Enviar invitación"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editUser && (
        <Modal onClose={() => setEditUser(null)} title="Editar nombre">
          <form onSubmit={saveEdit} className="space-y-4" data-testid="edit-user-modal">
            <In label="Nombre" testId="edit-user-name" value={editUser.name} onChange={(v) => setEditUser({ ...editUser, name: v })} />
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setEditUser(null)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" disabled={editSaving} data-testid="save-edit-user-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">
                {editSaving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {openCleanup && (
        <CleanupModal onClose={(changed) => { setOpenCleanup(false); if (changed) load(); }} />
      )}

      {resetInfo && (
        <Modal onClose={() => setResetInfo(null)} title="Enlace de recuperación">
          <div className="space-y-4" data-testid="reset-link-modal">
            <div className={`text-xs px-3 py-2.5 rounded-sm ${resetInfo.email_sent ? "badge-success" : "badge-pending"}`} data-testid="reset-link-status">
              {resetInfo.email_sent
                ? `✓ Correo enviado a ${resetInfo.email}. También puedes compartir el enlace directamente:`
                : `⚠ El correo no pudo enviarse a ${resetInfo.email}. Comparte el enlace manualmente:`}
            </div>
            <div className="px-3 py-2.5 rounded-sm text-[11px] font-mono break-all" style={{ background: "var(--bg-2)", border: "1px solid var(--border-2)", color: "var(--cyan)" }} data-testid="reset-link-url">
              {resetInfo.reset_link}
            </div>
            <div className="text-[11px] text-slate-500">El enlace es válido por 24 horas y solo puede usarse una vez. Los enlaces anteriores quedaron invalidados.</div>
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setResetInfo(null)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cerrar</button>
              <button
                type="button"
                data-testid="reset-link-copy"
                onClick={async () => { try { await navigator.clipboard.writeText(resetInfo.reset_link); toast.success("Enlace copiado"); } catch { toast.error("No se pudo copiar"); } }}
                className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1.5"
              >
                <Copy className="w-3.5 h-3.5" /> Copiar
              </button>
              <button
                type="button"
                data-testid="reset-link-whatsapp"
                onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Hola ${resetInfo.name}, aquí tienes el enlace para restablecer tu contraseña de INCONTI ERP (válido 24 horas): ${resetInfo.reset_link}`)}`, "_blank")}
                className="px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm"
                style={{ background: "#25D366", color: "#06220f" }}
              >
                WhatsApp
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
